
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import SelecionarEmpresa from '@/components/MeuSistema/Empresas/SelecionarEmpresa.vue';
import ServicoVeiculoCor from '@/servicos/Cadastros/Veiculo/ServicoVeiculoCor';
import { IVeiculoCor } from '@/models/Entidades/Cadastros/Veiculos/IVeiculoCor';
import SelecionarCorDenatran from '@/components/Cadastros/Veiculos/SelecionarCorDenatran.vue';

export default defineComponent({
  name: 'VeiculoCorModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    RequisicaoModal,
    SelecionarEmpresa,
    SelecionarCorDenatran,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherPermissoesDados, preencherEmpresasComEstrategiaPermissaoDados,
      apresentarMensagemSucesso, defineEmpresasSelecionadasCadastroCompartilhado,
      filtrarPermissaoDadosUsuarioMultiEmpresas, verificaConceitoParaApresentarEmpresas,
    } = useTelaBase();

    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);

    const servicoVeiculoCor = new ServicoVeiculoCor();
    telaBase.identificadorRecurso = 'CADASTRO_VEICULO_COR';
    telaBase.identificadorPermissao = 'PER_CADASTRO_VEICULO_COR';

    const state = reactive({ cor: {} as IVeiculoCor });

    function objetoInicial() {
      state.cor = {} as IVeiculoCor;
      state.cor.codigo = 0;
      state.cor.ativo = true;
      telaBase.empresasSelecionadas = [];
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };
      apresentarBarraProgresso();

      state.cor.codigoEmpresa = telaBase.empresasSelecionadas?.[0] ?? props.operacao.empresaSelecionada;

      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) retorno = await servicoVeiculoCor.incluir(state.cor);
      else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) retorno = await servicoVeiculoCor.alterar(state.cor);

      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        sincronizarRegistro(props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir ? EPermissaoDados.Incluir : EPermissaoDados.Alterar,
          props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir ? retorno.codigoRegistro : props.operacao.codigoRegistro);
        apresentarMensagemSucesso(retorno.mensagem);

        if (salvarNovo) {
          objetoInicial();
          telaBase.empresasSelecionadas = [props.operacao.empresaSelecionada];
          defineNovaOperacao(props.operacao);
        } else modalBase.computedVisivel = false;
      } else apresentarRetornoRequisicao(retorno);
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();

      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        else await obterPermissoes(ETipoPermissao.Dados);

        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);
        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);

        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.cor = await servicoVeiculoCor.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          telaBase.empresasSelecionadas = state.cor.codigoEmpresa ? [state.cor.codigoEmpresa] : [props.operacao.empresaSelecionada];
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }

        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        verificaConceitoParaApresentarEmpresas();
      }

      telaBase.carregando = false;
    });

    return {
      telaBase, props, modalBase, state, salvar, objetoInicial, EPermissaoDados,
    };
  },
});
